import React from 'react'
import PropTypes from 'prop-types'
import { Info, PencilSimple } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Badge } from '@/components/ui/badge'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { getAssetLink } from '../../../helpers/get-asset-link'
import ImageField from '../../FormLegacy/FormFields/ImageField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../Icons'
import { formatPhone, getPhoneMaxLength } from '../helpers'

import ClientActionsMenu from './ClientActionsMenu'

const ShowcaseSection = ({
  editModeList,
  isDisabled,
  userRole,
  isSubmitting,
  values,
  errors,
  touched,
  newClientData,
  handleChange,
  setFieldValue,
  setEditModeList,
  setFieldTouched,
  clearField,
  handleUpdateClientActiveStatus,
  setSubmitting,
  handleDelete,
  deletePending,
  confirmClient,
  rejectClient,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  return (
    <div
      className={`client-short-description-wrapper ${editModeList.length ? 'in-edit-mode' : ''}`}
    >
      <div className="image-container">
        <ImageField
          formType={'clientProfile'}
          fieldName={'avatar'}
          imageRatio={1}
          label={t('clients-legacy:profileView.showcase.avatarLabel')}
          showImageBeforeChange={!editModeList.includes('avatar')}
          callbackAfterChange={() => setFieldValue('clientAvatar', 'added')}
          image={getAssetLink({
            path: 'chitown-avatar/avatar',
            file: newClientData.user ? newClientData.user.avatar : '',
          })}
          defaultImage={getAssetLink({
            path: 'chitown-avatar/avatar',
            file: newClientData.user?.avatar,
          })}
          isDisabled={isDisabled || isSubmitting}
        />
      </div>
      <div className="decription-container">
        {editModeList.includes('name') ||
        editModeList.includes('email') ||
        editModeList.includes('phone') ? (
          <React.Fragment>
            <div className="columns">
              <div className="separate-column">
                <div className="column">
                  <TextField
                    name={'firstName'}
                    placeholder={t('clients:generalInformation.firstName.label')}
                    value={values.firstName}
                    isError={touched.firstName && !!errors.firstName}
                    errorLabel={touched.firstName && errors.firstName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('firstName')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                  <span className="cross-icon" onClick={() => clearField('firstName')}>
                    <Icon icon="cross" viewBox="0 0 23 20" />
                  </span>
                </div>
                <span
                  className="refresh-icon"
                  onClick={() => setFieldValue('firstName', newClientData.user?.firstName)}
                >
                  <Icon icon="refresh" viewBox="0 0 23 20" />
                </span>
              </div>
              <div className="separate-column">
                <div className="column">
                  <TextField
                    name={'lastName'}
                    placeholder={t('clients:generalInformation.lastName.label')}
                    value={values.lastName}
                    isError={touched.lastName && !!errors.lastName}
                    errorLabel={touched.lastName && errors.lastName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('lastName')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                  <span className="cross-icon" onClick={() => clearField('lastName')}>
                    <Icon icon="cross" viewBox="0 0 23 20" />
                  </span>
                </div>
                <span
                  className="refresh-icon"
                  onClick={() => setFieldValue('lastName', newClientData.user?.lastName)}
                >
                  <Icon icon="refresh" viewBox="0 0 23 20" />
                </span>
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <TextField
                  name={'email'}
                  placeholder={t('clients-legacy:table.columns.email')}
                  value={values.email}
                  isError={touched.email && !!errors.email}
                  errorLabel={touched.email && errors.email}
                  disabled
                />
              </div>
              <span className="refresh-icon">
                <div data-tip data-for="pause-points-tip">
                  <Info style={{ margin: 0 }} size={22} />
                  <ReactTooltip className="tooltip" id="pause-points-tip" multiline place="top">
                    {t('clients-legacy:profileView.showcase.emailPmsTooltip')}
                  </ReactTooltip>
                </div>
              </span>
            </div>
            <div className="columns">
              <div className="column">
                <TextField
                  type="string"
                  name={'phone'}
                  placeholder={t('clients-legacy:table.columns.phoneNumber')}
                  value={values.phone || ''}
                  isError={touched.phone && !!errors.phone}
                  errorLabel={touched.phone && errors.phone}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  maxLength={getPhoneMaxLength()}
                />
                <span className="cross-icon" onClick={() => clearField('phone')}>
                  <Icon icon="cross" viewBox="0 0 23 20" />
                </span>
              </div>
              <span
                className="refresh-icon"
                onClick={() =>
                  setFieldValue('phone', newClientData.phone ? String(newClientData.phone) : null)
                }
              >
                <Icon icon="refresh" viewBox="0 0 23 20" />
              </span>
            </div>
          </React.Fragment>
        ) : (
          <div className="general-information-row">
            <div className="general-information-col">
              <div className="information-element">
                {`${newClientData.user?.firstName} ${newClientData.user?.lastName}`}
                {(!isDisabled || isSubmitting) && (
                  <span
                    className="editPen-icon"
                    onClick={() => setEditModeList([...editModeList, 'name'])}
                  >
                    <PencilSimple size={12} weight="fill" />
                  </span>
                )}
              </div>
              {selectedBuilding?.appUserStatusAvailable && (
                <div className="information-element">
                  <Badge
                    color={newClientData.isAppUser ? 'green' : 'red'}
                    variant="light"
                    size="small"
                  >
                    {newClientData.isAppUser ? t('common:booleans.yes') : t('common:booleans.no')}
                  </Badge>
                </div>
              )}
              <div className="information-element">{newClientData.user?.email}</div>
              <div className="information-element">
                {formatPhone(newClientData.phone)}
                {(!isDisabled || isSubmitting) && (
                  <span
                    className="editPen-icon"
                    onClick={() => setEditModeList([...editModeList, 'phone'])}
                  >
                    <PencilSimple size={12} weight="fill" />
                  </span>
                )}
              </div>
            </div>
            <div className="menu-container">
              {!isDisabled && !newClientData.user?.isActive && (
                <div className="buttons">
                  <button
                    type="submit"
                    className={`button button-primary is-block ${
                      isSubmitting || deletePending ? 'is-loading' : ''
                    }`}
                    onClick={confirmClient}
                    disabled={isSubmitting || isDisabled || deletePending}
                  >
                    {t('common:actions.confirm')}
                  </button>
                  <button
                    type="submit"
                    className={`button button-gray is-block ${
                      isSubmitting || deletePending ? 'is-loading' : ''
                    }`}
                    onClick={rejectClient}
                    disabled={isSubmitting || isDisabled || deletePending}
                  >
                    {t('clients-legacy:profileView.rejectButton')}
                  </button>
                </div>
              )}
              <ClientActionsMenu
                newClientData={newClientData}
                userRole={userRole}
                setFieldValue={setFieldValue}
                handleUpdateClientActiveStatus={handleUpdateClientActiveStatus}
                setSubmitting={setSubmitting}
                handleDelete={handleDelete}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

ShowcaseSection.propTypes = {
  editModeList: PropTypes.array,
  isDisabled: PropTypes.bool,
  userRole: PropTypes.string,
  isSubmitting: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  newClientData: PropTypes.object,
  deletePending: PropTypes.bool,
  confirmClient: PropTypes.func,
  rejectClient: PropTypes.func,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  setEditModeList: PropTypes.func,
  setFieldTouched: PropTypes.func,
  clearField: PropTypes.func,
  handleUpdateClientActiveStatus: PropTypes.func,
  setSubmitting: PropTypes.func,
  handleDelete: PropTypes.func,
}

export default ShowcaseSection
